import React from "react"
import type { KpiSummary, GroupRow } from "~/api/snapshot_summaries"
import { formatCurrency, formatTvpi, formatIrr } from "./utils"

interface KpiStripProps {
  kpi: KpiSummary
  totals: GroupRow
}

interface DeltaResult {
  text: string
  direction: "up" | "down" | "flat"
}

function delta(
  current: number | null,
  prior: number | null,
  format: (v: number) => string,
  positiveIsGood = true,
): DeltaResult | null {
  if (current == null || prior == null) return null
  const diff = current - prior
  if (diff === 0) return { text: "—", direction: "flat" }
  const arrow = diff > 0 ? "▲" : "▼"
  const direction =
    diff > 0 ? (positiveIsGood ? "up" : "down") : positiveIsGood ? "down" : "up"
  return { text: `${arrow} ${format(Math.abs(diff))}`, direction }
}

export default function KpiStrip({ kpi, totals }: KpiStripProps) {
  const navDeltaPct = totals.quarter_activity?.delta_mv_pct
  const navDelta: DeltaResult | null =
    navDeltaPct != null
      ? {
          text: `${navDeltaPct >= 0 ? "▲" : "▼"} ${Math.abs(
            Math.round(navDeltaPct * 100),
          )}% QoQ`,
          direction: navDeltaPct >= 0 ? "up" : "down",
        }
      : null

  const irrDeltaBps: DeltaResult | null = (() => {
    if (kpi.irr == null || kpi.prior_irr == null) return null
    const bps = Math.round((kpi.irr - kpi.prior_irr) * 100)
    if (bps === 0) return { text: "—", direction: "flat" }
    const arrow = bps > 0 ? "▲" : "▼"
    return {
      text: `${arrow} ${Math.abs(bps)} bps`,
      direction: bps > 0 ? "up" : "down",
    }
  })()

  const moicDelta = delta(kpi.moic, kpi.prior_moic, (v) => `${v.toFixed(2)}x`)

  const realizedDelta = delta(kpi.realized_pl_q, kpi.prior_realized_pl_q, (v) =>
    formatCurrency(v),
  )

  const unrealizedDelta = delta(
    kpi.unrealized_pl_q,
    kpi.prior_unrealized_pl_q,
    (v) => formatCurrency(v),
  )

  const cards = [
    {
      label: "Total NAV",
      value: formatCurrency(kpi.nav),
      delta: navDelta,
    },
    {
      label: "Net IRR (ITD)",
      value: formatIrr(kpi.irr),
      delta: irrDeltaBps,
    },
    {
      label: "Blended MOIC",
      value: formatTvpi(kpi.moic),
      delta: moicDelta,
    },
    {
      label: "Realized P&L (Q)",
      value: formatCurrency(kpi.realized_pl_q),
      delta: realizedDelta,
    },
    {
      label: "Unrealized P&L (Q)",
      value: formatCurrency(kpi.unrealized_pl_q),
      delta: unrealizedDelta,
    },
  ]

  return (
    <div className="b-kpi-strip">
      {cards.map((card) => (
        <div key={card.label} className="b-kpi-card">
          <div className="b-kpi-label">{card.label}</div>
          <div className="b-kpi-value">{card.value}</div>
          {card.delta != null ? (
            <div className={`b-kpi-delta b-kpi-delta--${card.delta.direction}`}>
              {card.delta.text}
            </div>
          ) : (
            <div className="b-kpi-delta b-kpi-delta--flat">—</div>
          )}
        </div>
      ))}
    </div>
  )
}
